<template>
  <div class="plaid">
    <AppSpinner
      v-if="loading"
      class="m-auto"
      :color="hexColor()"
    />
    <div class="plaid__iframe-container">
      <iframe
        v-if="linkToken && !submittingDocs"
        v-show="!loading && !showAnimation"
        class="plaid__iframe"
        :src="iframeSource"
        frameborder="0"
        height="600"
        width="100%"
        @load="handleLoad"
      />
      <div
        v-if="submittingDocs"
        class="text-center min-h-600 flex items-center"
      >
        <AppSpinner
          class="m-auto"
          :color="hexColor()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import AppSpinner from '@/components/common/spinners/AppSpinner'
import theme from '@/mixins/theme'
import tracking from '@/mixins/tracking'
import offers from '@/mixins/offers'
import Axios from '@/utilities/axios-instance'
import 'url-polyfill'
import * as ROUTE_PATHS from '@/constants/route-paths'

const PLAID_RBC = 'RBC Royal Bank'
const PLAID_NBC = 'National Bank of Canada'
const PLAID_TD = 'TD Canada Trust'

export default {
  // Name
  name: 'PlaidIframe',

  // Components
  components: {
    AppSpinner
  },

  mixins: [
    theme,
    offers,
    tracking
  ],

  // Props
  props: {
    docsStandAlone: {
      type: Boolean,
      default: false
    },

    theme: {
      type: String,
      default: 'blue'
    },

    eventPrefix: {
      type: String,
      default: 'ploan'
    },

    institutionId: {
      type: String,
      default: ''
    }
  },

  // Data
  data () {
    return {
      linkToken: undefined,
      loading: false,
      documentsUrl: process.env.VUE_APP_API_DOCUMENTS_URL,
      loginStartTime: undefined,
      bank: '',
      showAnimation: false,
      publicToken: undefined,
      submittingDocs: false,
      metaData: undefined
    }
  },

  // Computed
  computed: {
    // used for non-documents standalone flow only
    cduid () {
      if (this.docsStandAlone) return
      return this.$store.state.application.cduid
    },

    // used for documents standalone flow only
    oppId () {
      if (!this.docsStandAlone) return
      return this.$store.state.account.oppId
    },

    // used for documents standalone flow only
    accountId () {
      if (!this.docsStandAlone) return
      return this.$store.state.account.accountId
    },

    iframeSource () {
      return `${process.env.VUE_APP_PLAID_BASE_URL}?link=${this.linkToken}&institutionId=${this.institutionId}`
    },

    sessionid () {
      return this.$store.state.application.sessionId
    },

    isOffer () {
      return this.$store.getters['offers/isOffer']
    },

    returningCustomer () {
      return this.$store.state.returningCustomer.is_in_progress
    },

    crm () {
      return this.$store.state.account.crm
    }
  },

  // Created
  async created () {
    this.loading = true
    window.addEventListener('message', this.handleMessageEvent)
    window.addEventListener('touchstart', {}) // Fix for weird iOS iframe scroll / click bug
    let data
    if (this.docsStandAlone) {
      data = {
        'Opportunity__c': this.oppId
      }
    } else {
      data = {
        cduid: this.cduid
      }
    }
    this.fetchLinkToken(data)
  },
  mounted () {
    document.addEventListener('mousedown', this.preventIframeAutofocus)
  },

  // Before Destroy
  beforeDestroy () {
    window.removeEventListener('message', this.handleMessageEvent)
    document.removeEventListener('mousedown', this.preventIframeAutofocus)
  },

  // Methods
  methods: {
    preventIframeAutofocus (event) {
      event.preventDefault()
    },
    async fetchLinkToken (data) {
      const response = await Axios.request({
        method: 'POST',
        url: `${process.env.VUE_APP_LEAD_API_PERSONAL_LOANS_BASE_URL}plaid-create-link-token`,
        data: data
      })
      this.linkToken = response.data.data.link_token
    },

    async handleLoad () {
      if (this.linkToken) this.loading = false
      this.$emit('loaded')
    },

    /**
     * Handle the Message event from the iframe
     * (Other sources can throw these messages too, such as Vue Dev tools)
     * @param {e} - event
     */
    handleMessageEvent (e) {
      const { source, event, data } = e.data
      if (source !== 'plaid') return
      switch (event) {
        case 'onSuccess':
          this.handlePlaidSuccess(data)
          break
        case 'onEvent':
          this.handlePlaidEvent(data)
          break
        case 'onExit':
          this.handlePlaidExit(data)
          break
      }
    },

    /**
     * Handle Data layer
     */
    handleDatalayer (datalayerObject) {
      dataLayer.push(datalayerObject)
    },

    /**
     * Handle Plaid Event
     * @param {Object} - data
     */
    async handlePlaidEvent (data) {
      let dataLayerObj
      switch (data.eventName) {
        case 'OPEN':
          const linkSessionId = data.metadata.link_session_id
          dataLayerObj = {
            event: `spring_${this.eventPrefix}_plaid_open`,
            link_session_id: linkSessionId
          }
          break
        case 'ERROR':
          dataLayerObj = {
            event: `spring_${this.eventPrefix}_plaid_error`,
            plaid_error_code: data.metadata.error_code
          }
          this.handleDatalayer(dataLayerObj)
          this.handlePlaidTiming(data.metadata.error_code)
          break
        case 'SUBMIT_MFA':
          dataLayerObj = { event: `spring_${this.eventPrefix}_plaid_submit_mfa` }
          this.handleDatalayer(dataLayerObj)
          break
        case 'SUBMIT_CREDENTIALS':
          this.loginStartTime = Math.round(Date.now() / 1000)
          dataLayerObj = { event: `spring_${this.eventPrefix}_plaid_submit_credential` }
          if (this.eventPrefix === 'ploan') window.rudderanalytics.track('spring_ploan_plaid_submit_credential')
          this.handleDatalayer(dataLayerObj)
          break
        case 'SELECT_INSTITUTION':
          this.bank = data.metadata.institution_name
          dataLayerObj = {
            event: `spring_${this.eventPrefix}_plaid_financial_institution_selected`,
            selected_financial_institution: data.metadata.institution_name
          }
          this.handleDatalayer(dataLayerObj)

          if (this.bank === PLAID_RBC) this.handleSwitchToFlinks(3)
          else if (this.bank === PLAID_NBC) this.handleSwitchToFlinks(7)
          else if (this.bank === PLAID_TD) this.handleSwitchToFlinks(4)
          break
        case 'TRANSITION_VIEW':
          switch (data.metadata.view_name) {
            case 'ERROR':
              dataLayerObj = { event: `spring_${this.eventPrefix}_plaid_component_load_error` }
              this.handleDatalayer(dataLayerObj)
              break

            case 'DATA_TRANSPARENCY_CONSENT':
              dataLayerObj = { event: `spring_${this.eventPrefix}_plaid_component_consent` }
              this.handleDatalayer(dataLayerObj)
              break

            case 'SELECT_INSTITUTION':
              dataLayerObj = { event: `spring_${this.eventPrefix}_plaid_component_institution_selector` }
              this.handleDatalayer(dataLayerObj)
              break

            case 'CREDENTIAL':
              dataLayerObj = { event: `spring_${this.eventPrefix}_plaid_component_load_credential` }
              this.handleDatalayer(dataLayerObj)
              break

            case 'EXIT':
              dataLayerObj = { event: `spring_${this.eventPrefix}_plaid_exit_prompt` }
              this.handleDatalayer(dataLayerObj)
              break

            case 'MFA':
              dataLayerObj = { event: `spring_${this.eventPrefix}_plaid_component_load_mfa` }
              this.handleDatalayer(dataLayerObj)
              break

            case 'SELECT_ACCOUNT':
              dataLayerObj = { event: `spring_${this.eventPrefix}_plaid_select_account` }
              this.handleDatalayer(dataLayerObj)
              break
          }
          break
        case 'EXIT':
          dataLayerObj = { event: `spring_${this.eventPrefix}_plaid_exit` }
          this.handleDatalayer(dataLayerObj)
          break
        case 'HANDOFF':
          dataLayerObj = { event: `spring_${this.eventPrefix}_plaid_handoff` }
          this.handleDatalayer(dataLayerObj)
          this.handlePlaidTiming('HANDOFF')
          if (this.isOffer) {
            const eContract = this.$store.state.offers.opportunity.id
            this.$_offers_submitPlaidData(eContract, data.metadata, this.publicToken)
          } else {
            // if we have token submit plaid data
            if (this.publicToken) {
              await this.submitPlaidInfo(data.metadata)
            } else {
              this.metaData = data.metadata
            }
          }
          break
      }
    },

    /**
     * Handle Plaid Success Event
     * Set publicToken
     * @param {Object} - data
     */
    async handlePlaidSuccess (data) {
      // set token
      this.publicToken = data.publicToken

      // if handoff fired already, submit plaid data
      if (this.metaData) {
        await this.submitPlaidInfo(this.metaData)
      }
    },

    handleSwitchToFlinks (institutionId) {
      this.$emit('switch-to-flinks', institutionId)
    },

    /**
     * Handle Plaid Exit Event
     * @param {Object} - data
     */
    handlePlaidExit (data) {
      if (!process.env.VUE_APP_DISABLE_FI_SELECTOR) {
        this.$router.push({
          path: ROUTE_PATHS.FINANCIAL_SELECTOR
        })
      } else {
        if (data.metadata.status) this.$emit('exit')
      }
    },

    /**
     * Submit Plaid Info
     * Grab the info and make the request to the Api
     * @emits verified
     */
    async submitPlaidInfo (messageData) {
      try {
        this.submittingDocs = true
        let data = new FormData()
        let source
        data.append('Type__c', 'Bank Statements')
        if (this.docsStandAlone) {
          data.append('Additional_Documents_Submitted__c', true)
          if (this.accountId) data.append('Account__c', this.accountId)
          if (this.oppId) data.append('Opportunity__c', this.oppId)
          source = 'Agent Triggered'
          data.append('crm', this.crm)
        } else {
          if (this.cduid) data.append('cduid', this.cduid)
          source = this.returningCustomer ? 'Returning Customer' : 'Initial Application'
        }
        data.append('public_token', this.publicToken)
        data.append('institution_id', messageData.institution_id)
        data.append('institution_name', messageData.institution_name)
        data.append('link_session_id', messageData.link_session_id)
        data.append('request_id', messageData.request_id)
        data.append('timestamp', messageData.timestamp)
        data.append('Source__c', source)
        if (!this.cduid) data.append('crm', this.crm)
        await Axios.request({
          method: 'POST',
          url: this.documentsUrl,
          data: data
        })
        this.$emit('verified')

        window.rudderanalytics.track(`spring_${this.eventPrefix}_bankdetails_step_completed`, {
          bank: messageData.institution_name,
          lead_source: this.leadSource,
          device: this.deviceType,
          returning_customer: this.returningCustomer
        })
      } catch (error) {
        console.warn(error)
      }
    },

    handlePlaidTiming (plaidCode) {
      // NOT WORKING UNTIL WE GET REAL TIME EVENTS ENABLED
      const loginEndTime = Math.round(Date.now() / 1000)
      const time = loginEndTime - this.loginStartTime
      const date = new Date()
      const timestamp =
      `${date.getUTCFullYear()}-${date.getUTCMonth() + 1}-${date.getUTCDate()} ${date.getUTCHours()}:${date.getUTCMinutes()}:${date.getUTCSeconds()}.${date.getUTCMilliseconds()}`
      const plaidObj = {
        event: 'spring_plaid_timing',
        plaid_login_time: time,
        response_code: plaidCode,
        session_id: this.sessionid,
        cduid: this.cduid,
        bank: this.bank,
        plaid_login_timestamp: timestamp
      }
      dataLayer.push(plaidObj)
    },

    handleRelaunchPlaid () {
      this.$emit('enter')
    }
  }
}
</script>

<style scoped lang="scss">
.plaid {
  @apply m-auto max-w-[365px];

  &__loading {
    @apply text-center;

    color: #9d9a9a;
    margin-top: 15px;
  }

  &__iframe-container {
    @apply relative;

    -webkit-overflow-scrolling: touch;
  }

  &__iframe {
    @apply border-2 border-solid border-gray-300;
    @apply rounded-20;
  }
}
</style>
